import { useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
// Redux
import { useDispatch, useSelector } from 'react-redux';
// Pages
import appPages from './pages';
// Utils
import { fetchWithoutToken } from '../../utils/fetch';
// Slice
import { setActiveLoginModal } from '../../app/features/userSlice';
import { setInterestingLinks } from '../../app/features/interestingLinksSlice';
import { setAllArtSources } from '../../app/features/artSlice';
import { setAllBooksSources } from '../../app/features/booksSlice';
import { setAllPatentsSources } from '../../app/features/patentsSlice';
import { setAllLibrarySources } from '../../app/features/librarySlice';
import { setBaseNews, setResetBaseNews } from '../../app/features/newsSlice';
import {
	setPublicToken,
	setPublicTokenExpires,
} from '../../app/features/authSlice';
import {
	setDatabases,
	setInstitutionCode,
	setInstitutionLogo,
	setTopics,
	setRequestLogin,
	setUniqueLogin,
	setAssignedLogin,
	setEbooksAccess,
	setIsMedicalInstitution,
	setLanguageInstitution,
	setIsActive
} from '../../app/features/institutionSlice';
// Services
import { getNews } from '../../services/getNews';
import { getInterestingLinks } from '../../services/getInterestingLinks';
import { getInstitutionTopicsAndDatabases } from '../../services/getInstitutionTopicsAndDatabases';
import { isCosmetology } from '../../utils/isCosmetology';
import { sectionsToRemoveInCosmetology } from '../../services/getSectionsInstitutions';
import { isTokenExpired } from '../../utils/isTokenExpired';
// Translate
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';

const Router = () => {
	const { i18n } = useTranslation();
	const { languageInstitution, isActive } = useSelector((state) => state.institution);
	const publicPages = [
		'',
		'help',
		'login',
		'recover',
		'signup',
		'saml-callback',
		'd2l-callback',
		'login-with-wordpress',
	];

	useEffect(() => {
		i18n.changeLanguage(languageInstitution);
		dispatch(setLanguageInstitution(languageInstitution));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [languageInstitution]);

	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { pathname } = useLocation();
	const currentlyRoute = pathname.replace('/', '');

	// Datos de autenticación
	const USERNAME = process.env.REACT_APP_AUTH_USERNAME;
	const PASSWORD = process.env.REACT_APP_PASSWORD;
	const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT;
	const INSTITUTION_CODE = process.env.REACT_APP_INSTITUTION_CODE;
	const isCosmetologyInstitution = isCosmetology(INSTITUTION_CODE);

	const loadInstitutionData = async (code) => {
		try {
			const newsResponse = await getNews(code);
			dispatch(setResetBaseNews());
			const newsFiltered = newsResponse.data.filter(
				(item) => item.title !== '[Removed]'
			);
			dispatch(setBaseNews(newsFiltered));
		} catch (error) {
			dispatch(setBaseNews([]));
		}

		try {
			const resourcesResponse = await getInterestingLinks(code);
			dispatch(setInterestingLinks(resourcesResponse.data.data));
		} catch (error) {
			dispatch(setInterestingLinks([]));
		}

		try {
			const topicsResponse = await getInstitutionTopicsAndDatabases(code);
			dispatch(setTopics(topicsResponse.data.categories));
			dispatch(setDatabases(topicsResponse.data.sources));
		} catch (error) {
			dispatch(setTopics([]));
			dispatch(setDatabases([]));
		}
	};

	const authenticateUser = async () => {
		try {
			const response = await fetchWithoutToken.post('/auth', {
				username: USERNAME,
				password: PASSWORD,
				code: ENVIRONMENT === 'PRODUCTION' ? null : INSTITUTION_CODE,
			});

			const {
				access_token,
				expires_in,
				code,
				logo,
				requestLoginInstitution,
				uniqueLoginInstitution,
				// all sources
				academic_sources,
				books_sources,
				gallery_sources,
				patents_sources,
				assignedLogin,
				ebooks,
				is_medical,
				language,
				is_active
			} = response.data;

			const uniqueBooksSources = [...new Set(books_sources)];

			localStorage.setItem('token', access_token);
			localStorage.setItem('expires_in', expires_in);
			dispatch(setInstitutionCode(code));
			dispatch(setAssignedLogin(assignedLogin));
			dispatch(setInstitutionLogo(logo));
			dispatch(setIsMedicalInstitution(is_medical));
			dispatch(setPublicToken(access_token));
			dispatch(setPublicTokenExpires(expires_in));
			dispatch(setRequestLogin(requestLoginInstitution));
			dispatch(setUniqueLogin(uniqueLoginInstitution));
			dispatch(setAllLibrarySources(academic_sources));
			dispatch(setAllBooksSources(uniqueBooksSources));
			dispatch(setAllArtSources(gallery_sources));
			dispatch(setAllPatentsSources(patents_sources));
			dispatch(setEbooksAccess(ebooks));
			dispatch(setIsActive(is_active));
			languageInstitution === null &&
				dispatch(setLanguageInstitution(language));
			//  Solicitando datos de la institucion
			await loadInstitutionData(code);
		} catch (error) {
			console.error('Error during authentication', error);
		}
	};

	const { isUserLoggedInAndHasLoggingSettingsActive } = useAuth();

	useEffect(() => {
		if(!isActive &&  currentlyRoute !=='' ) {
			dispatch(setActiveLoginModal(true));
			navigate('/');
		}

		if (currentlyRoute === '' || isTokenExpired(localStorage.expires_in)) {
			authenticateUser();
		}

		if (isCosmetologyInstitution) {
			if ([...sectionsToRemoveInCosmetology].includes(currentlyRoute)) {
				navigate('/media');
				return;
			}

			(currentlyRoute === 'media' || currentlyRoute === '') &&
				authenticateUser();
			return;
		}

		if (!isUserLoggedInAndHasLoggingSettingsActive()) {
			if (publicPages.includes(currentlyRoute)) {
				return;
			}

			dispatch(setActiveLoginModal(true));
			navigate('/');
			return;
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentlyRoute, isCosmetologyInstitution]);

	const pageRoutes = appPages.map(({ path, title, element }) => {
		return <Route key={title} path={`/${path}`} element={element} />;
	});

	return <Routes>{pageRoutes}</Routes>;
};

export default Router;
